import { useEffect, useRef, useState } from "react";
import { Bell, CalendarClock, CheckCircle2, Clock, Sparkles, ArrowRightCircle } from "lucide-react";
import { http, notify } from "../api";

const REFRESH_INTERVAL_MS = 60_000;
const DIGEST_KEY = "dsa-reminder-digest";

const todayKey = () => new Date().toISOString().slice(0, 10);

/**
 * Follow-up reminder bell for the topbar.
 * Polls /api/reminders every 60s; opens a dropdown with three buckets:
 * Overdue · Today · Upcoming (next 3 working days).
 * Actions: click a row to open the lead, snooze 24h, or mark done.
 * On the first load of each day it auto-opens as a "morning digest" so users
 * see everything due today before starting.
 */
export default function ReminderBell({ user, onOpenLead }) {
  const [data, setData] = useState({ overdue: [], today: [], upcoming: [], handoffs: [], counts: { overdue: 0, today: 0, upcoming: 0, handoffs: 0 } });
  const [open, setOpen] = useState(false);
  const [digestMode, setDigestMode] = useState(false);
  const wrapper = useRef(null);
  const digestChecked = useRef(false);

  const load = () => {
    http.get("/reminders")
      .then((r) => {
        setData(r.data);
        maybeShowDigest(r.data);
      })
      .catch(() => {});
  };

  const maybeShowDigest = (payload) => {
    if (digestChecked.current) return;
    if (!user?.id) return;
    digestChecked.current = true;
    const key = `${DIGEST_KEY}:${user.id}`;
    const seen = window.localStorage.getItem(key);
    if (seen === todayKey()) return;
    const hasItems = (payload.counts?.overdue || 0) + (payload.counts?.today || 0) + (payload.counts?.handoffs || 0) > 0;
    if (!hasItems) {
      window.localStorage.setItem(key, todayKey());
      return;
    }
    // First reminders load of the day + something to act on → auto-open as digest
    window.localStorage.setItem(key, todayKey());
    setDigestMode(true);
    setOpen(true);
  };

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (wrapper.current && !wrapper.current.contains(e.target)) {
        setOpen(false);
        setDigestMode(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const total = (data.counts?.overdue || 0) + (data.counts?.today || 0) + (data.counts?.handoffs || 0);

  const togglePanel = () => {
    setDigestMode(false);
    setOpen((v) => !v);
  };

  const snooze = async (fu) => {
    try {
      await http.post(`/reminders/${fu.id}/snooze`, { hours: 24 });
      notify.success("Snoozed by 24 hours");
      load();
    } catch (err) {
      notify.error(err, "Snooze failed");
    }
  };

  const done = async (fu) => {
    try {
      await http.post(`/reminders/${fu.id}/complete`, { outcome: "Completed" });
      notify.success("Follow-up marked done");
      load();
    } catch (err) {
      notify.error(err, "Could not mark done");
    }
  };

  const openLead = (leadId) => {
    setOpen(false);
    setDigestMode(false);
    onOpenLead?.(leadId);
  };

  const dismissHandoff = async (h) => {
    try {
      await http.post(`/reminders/handoffs/${h.id}/dismiss`);
      load();
    } catch (err) {
      notify.error(err, "Could not dismiss");
    }
  };

  const dismissAllHandoffs = async () => {
    try {
      const r = await http.post(`/reminders/handoffs/dismiss-all`);
      notify.success(`Cleared ${r.data.dismissed || 0} handoffs`);
      load();
    } catch (err) {
      notify.error(err, "Could not clear handoffs");
    }
  };

  return (
    <div className="reminder-wrap" ref={wrapper}>
      <button
        type="button"
        className={total ? "reminder-bell has-alerts" : "reminder-bell"}
        onClick={togglePanel}
        data-testid="reminder-bell"
        aria-label="Follow-up reminders"
      >
        <Bell size={17} />
        {total > 0 && (
          <span className="reminder-badge" data-testid="reminder-badge">
            {total > 99 ? "99+" : total}
          </span>
        )}
      </button>
      {open && (
        <div className="reminder-panel" data-testid="reminder-panel" role="dialog">
          {digestMode && (
            <div className="digest-banner" data-testid="reminder-digest-banner">
              <Sparkles size={15} />
              <div>
                <strong>Morning digest</strong>
                <small>
                  {data.counts.overdue > 0
                    ? `${data.counts.overdue} overdue · `
                    : ""}
                  {data.counts.today} due today
                  {data.counts.handoffs > 0 ? ` · ${data.counts.handoffs} new handoff${data.counts.handoffs === 1 ? '' : 's'}` : ""}.
                  {" "}Start with these, then snooze or close as you go.
                </small>
              </div>
            </div>
          )}
          <header>
            <div>
              <p className="eyebrow">FOLLOW-UPS</p>
              <h3>Reminders</h3>
            </div>
            <button className="outline-button compact" onClick={load} data-testid="reminder-refresh">
              Refresh
            </button>
          </header>

          {data.handoffs && data.handoffs.length > 0 && (
            <section className="reminder-section reminder-handoffs" data-testid="reminder-section-handoffs">
              <header>
                <strong>New handoffs</strong>
                <button
                  type="button"
                  className="chip-close"
                  onClick={dismissAllHandoffs}
                  data-testid="reminder-handoffs-dismiss-all"
                  title="Dismiss all"
                >
                  Clear all
                </button>
              </header>
              <ul>
                {data.handoffs.map((h) => (
                  <li key={h.id} data-testid={`reminder-handoff-${h.id}`}>
                    <button
                      type="button"
                      className="reminder-row"
                      onClick={() => openLead(h.lead_id)}
                      data-testid={`reminder-handoff-open-${h.id}`}
                    >
                      <div>
                        <strong>{h.customer_name || "Lead"}</strong>
                        <small className="mono">{h.lead_id}</small>
                      </div>
                      <div className="reminder-meta">
                        <span>
                          <ArrowRightCircle size={12} /> {h.bank_name || "—"} · {h.reason}
                        </span>
                        <span className="muted">from {h.actor_name || "System"}</span>
                      </div>
                    </button>
                    <div className="reminder-actions">
                      <button
                        type="button"
                        className="icon-button"
                        onClick={() => dismissHandoff(h)}
                        title="Dismiss"
                        data-testid={`reminder-handoff-dismiss-${h.id}`}
                        aria-label="Dismiss"
                      >
                        <CheckCircle2 size={13} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <Section
            title="Overdue"
            variant="overdue"
            items={data.overdue}
            onOpen={openLead}
            onSnooze={snooze}
            onDone={done}
            empty="You're all caught up on overdue calls."
          />
          <Section
            title="Today"
            variant="today"
            items={data.today}
            onOpen={openLead}
            onSnooze={snooze}
            onDone={done}
            empty="Nothing due today."
          />
          <Section
            title="Next 3 days"
            variant="upcoming"
            items={data.upcoming}
            onOpen={openLead}
            onSnooze={snooze}
            onDone={done}
            empty="No upcoming follow-ups scheduled."
          />
        </div>
      )}
    </div>
  );
}

function Section({ title, variant, items, empty, onOpen, onSnooze, onDone }) {
  return (
    <section className={`reminder-section reminder-${variant}`} data-testid={`reminder-section-${variant}`}>
      <header>
        <strong>{title}</strong>
        <span className="mono">{items.length}</span>
      </header>
      {items.length === 0 ? (
        <p className="empty compact-empty">{empty}</p>
      ) : (
        <ul>
          {items.map((f) => (
            <li key={f.id} data-testid={`reminder-item-${f.id}`}>
              <button
                type="button"
                className="reminder-row"
                onClick={() => onOpen(f.lead_id)}
                data-testid={`reminder-open-${f.id}`}
              >
                <div>
                  <strong>{f.lead?.customer_name || "Lead"}</strong>
                  <small className="mono">{f.lead_id}</small>
                </div>
                <div className="reminder-meta">
                  <span>
                    <CalendarClock size={12} /> {formatDue(f.due_at)}
                  </span>
                  <span className="muted">{f.followup_type || "Follow-up"}</span>
                </div>
              </button>
              <div className="reminder-actions">
                <button
                  type="button"
                  className="icon-button"
                  onClick={() => onSnooze(f)}
                  title="Snooze 24 hours"
                  data-testid={`reminder-snooze-${f.id}`}
                  aria-label="Snooze"
                >
                  <Clock size={13} />
                </button>
                <button
                  type="button"
                  className="icon-button"
                  onClick={() => onDone(f)}
                  title="Mark done"
                  data-testid={`reminder-done-${f.id}`}
                  aria-label="Mark done"
                >
                  <CheckCircle2 size={13} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function formatDue(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (isToday) return `Today · ${time}`;
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" }) + ` · ${time}`;
}
